/**
 * @module app.um
 */

import { autoinject, bindingMode, observable } from "aurelia-framework";
import { bindable } from 'aurelia-typed-observable-plugin';
import { Toaster } from 'app/shared/lib/toaster';
import { getReference, getReferences } from 'app/shared/models/essette-types';
import { apiCall } from 'app/shared/lib/api/api';
import { deepClone } from 'app/shared/lib/utilities';
import { AuthService } from 'app/um/services/auth-service';
import { LookupService } from 'app/cd/services/lookup-service';
import { DateComparison } from 'app/shared/lib/date-utilities';
import { ComponentBase, IForm } from 'app/shared/lib/essette-site';
import { FormatProvider } from 'app/shared/lib/formatting/format-provider';
import { maxDecimal } from 'app/shared/lib/number-utilities';
import { IAuthContextSettingsDetails } from 'app/um/lib/auth-context';
import { ActionOptions } from 'app/shared/lib/api/api-types';
import { IServiceChange } from 'app/pp/components/update/update-auth-details-step';
import * as UM from 'app/um/models/essette-api';
import * as CD from 'app/cd/models/essette-api';

export interface ISaveServiceChanges {
    service: UM.AuthService;
}

export interface ISaveServiceOptions {
    showStatus: boolean;
    showStatusReason: boolean;
    showQtyApproved: boolean;
    showApprovedCharges: boolean;
    showModifiers: boolean;
    portalEditMode?: boolean;
    pendServiceChanges?: boolean;
}

export const defaultOptions: ISaveServiceOptions = {
    showStatus: true,
    showStatusReason: true,
    showQtyApproved: true,
    showApprovedCharges: true,
    showModifiers: false,
    portalEditMode: false,
    pendServiceChanges: false
}

@autoinject()
export class SaveService extends ComponentBase implements IForm {

    ////****************** Fields and Properties ************************************////
    @bindable.object protected service: UM.AuthService = { id: null };
    @bindable.boolean protected readonly: boolean = false;

    @bindable.object()
    public serviceApprovedCharges: IAuthContextSettingsDetails | null = null;

    @bindable.object()
    public serviceRequestedCharges: IAuthContextSettingsDetails | null = null;

    @bindable.object()
    public serviceRequestedQuantity: IAuthContextSettingsDetails | null = null;

    @bindable.object()
    public servicesAuthDate: IAuthContextSettingsDetails | null = null;

    @bindable.object()
    public servicesExpDate: IAuthContextSettingsDetails | null = null;

    @bindable.objectNull()
    public servicesStatusReason: IAuthContextSettingsDetails | null = null;

    @bindable.objectNull()
    public servicesDental: IAuthContextSettingsDetails | null = null;

    @bindable.objectNull()
    protected auth: UM.Auth | null = null;

    @bindable.array()
    protected allTransitions: UM.AuthServiceStatus[] = [];

    @bindable.object({ defaultBindingMode: bindingMode.oneTime })
    public options: ISaveServiceOptions = defaultOptions;

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    protected addedFromPortalEditMode: boolean = false;

    @bindable({ defaultBindingMode: bindingMode.twoWay })
    protected pendingServiceChanges: IServiceChange[] = [];

    protected transitions: UM.AuthServiceStatus[] = [];
    protected bodySites: CD.BodySite[] = [];
    protected bodySubSites: CD.BodySubSite[] = [];

    protected modifierSearchQuery: string | null = null;   // the value of the autocomplete textbox
    private modifierSearchResults: CD.AuthModifier[] = [];   // array of search results from API for autocomplete

    @observable requestedCount: number = 1;
    @observable requestedCharge: number | null = null;
    @observable selectedStatus: UM.AuthServiceStatus | null = null;
    @observable selectedStatusReason: UM.AuthServiceStatusReason | null = null;
    @observable bodySite: CD.BodySite | null = null;
    @observable bodySubSite: CD.BodySubSite | null = null;

    protected showDates: boolean = false;
    protected hasValidDates: boolean = true;

    protected maxDecimal = maxDecimal;

    ////****************** Element References ***************************************////

    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly toaster: Toaster,
        private readonly authService: AuthService,
        private readonly lookupService: LookupService,
        private readonly formatProvider: FormatProvider,
        ...rest: any[]) {
        super(...rest);
    }

    protected bind(): void {
        this.reset();

        if(this.servicesDental) this.loadBodySites();

        if(!!this.service.quantity?.requested) {
            this.requestedCount = this.service.quantity.requested;
        }

        if(!!this.service.charge?.requested) {
            this.requestedCharge = this.formatProvider.parseNumber(this.service.charge.requested);
        }

        if(!!this.service.charge?.approved) {
            this.service.charge.approved = this.formatProvider.formatDecimal(Number.parseFloat(this.service.charge.approved), { format: 'fixed2' });
        }

        this.showDates = this.servicesAuthDate?.visible === true || this.servicesExpDate?.visible === true;
        if(this.showDates) {
            this.dateChanged();
        }
    }

    ////****************** Events ***************************************************////
    protected async serviceChanged(): Promise<void> {
        this.modifierSearchQuery = null;
        this.configureTransitions();

        if(this.service) {
            this.requestedCount = this.service.quantity?.requested || 1;

            if(!!this.service.charge?.requested) {
                this.requestedCharge = this.formatProvider.parseNumber(this.service.charge.requested);
            } else {
                this.requestedCharge = null;
            }

            if(!!this.service.charge?.approved) {
                this.service.charge.approved = this.formatProvider.formatDecimal(Number.parseFloat(this.service.charge.approved), { format: 'fixed2' });
            }

            this.bodySite = this.service.bodySite ?? null;
            this.bodySubSite = this.service.bodySubSite ?? null;

            if(!!this.service.status) {
                // capture reason first, if it exists, so we don't lose it when status changes
                if(!!this.service.status?.reason?.id) {
                    this.selectedStatusReason = deepClone(this.service.status.reason);
                } else {
                    this.selectedStatusReason = null;
                }

                this.selectedStatus = deepClone(this.service.status);
            } else {
                this.selectedStatus = null;
                this.selectedStatusReason = null;
            }

            if(!this.service.authorizedDate) {
                this.service.authorizedDate = this.auth?.serviceDate?.dateRange?.start;
            }
            if(!this.service.expirationDate) {
                this.service.expirationDate = this.auth?.serviceDate?.dateRange?.end;
            }
            this.dateChanged();
        }
    }

    protected async requestedCountChanged(newValue: number, oldValue: number): Promise<void> {
        if(!!this.service.status?.determination?.status?.determination) {
            switch(this.service.status.determination?.status?.determination.result) {
                case 'modified':
                    break;
                case 'approved':
                    if(!!this.service.quantity) {
                        this.service.quantity.approved = this.requestedCount;
                    }
                    break;
                case 'other':
                case 'denied':
                    if(!!this.service.quantity) {
                        this.service.quantity.approved = 0;
                    }
                    break;
            }
        }

        if(!!this.service.quantity) {
            this.service.quantity.requested = newValue;
        }

    }

    protected async requestedChargeChanged(newValue: number, oldValue: number): Promise<void> {
        if(!!this.service.status?.determination?.status?.determination) {
            switch(this.service.status.determination?.status.determination.result) {
                case 'modified':
                    break;
                case 'approved':
                    if(!!this.service.charge && this.serviceApprovedCharges?.visible) {
                        this.service.charge.approved = this.requestedCharge?.toString()
                    }
                    break;
                case 'other':
                case 'denied':
                    if(!!this.service.charge && this.serviceApprovedCharges?.visible) {
                        this.service.charge.approved = this.requestedCharge != null ? "0.00" : null;
                    }
                    break;
            }
        }

        if(!!this.service.charge) {
            this.service.charge.requested = newValue?.toString()
        }
    }

    protected selectedStatusChanged(newValue: UM.AuthServiceStatus, oldValue: UM.AuthServiceStatus): void {
        if(newValue && newValue.id) {
            if(!!this.service) {
                this.service.status = newValue;
                // if reason not found for selected status, clear out previously selected reason
                if(this.service.status.reasons && !this.service.status.reasons.find(r => r.id === this.selectedStatusReason?.id)) {
                    this.selectedStatusReason = null;
                }
                if(this.service.status.determination?.status?.determination?.result) {
                    switch(this.service.status.determination?.status.determination.result) {
                        case 'modified':
                            break;
                        case 'approved':
                            if(!!this.service.quantity) {
                                this.service.quantity.approved = this.requestedCount;
                            }
                            if(!!this.service.charge && this.serviceApprovedCharges?.visible) {
                                this.service.charge.approved = this.requestedCharge?.toString()
                            }
                            break;
                        case 'other':
                        case 'denied':
                            if(!!this.service.quantity) {
                                this.service.quantity.approved = 0;
                            }
                            if(!!this.service.charge && this.serviceApprovedCharges?.visible) {
                                this.service.charge.approved = this.requestedCharge != null ? "0.00" : null;
                            }
                            break;
                    }
                }
                if(!!this.service.quantity) {
                    this.service.quantity.requested = this.requestedCount;
                }
                if(!!this.service.charge && this.serviceRequestedCharges?.visible) {
                    this.service.charge.requested = this.requestedCharge?.toString()
                }

                this.dateChanged();
            }
        }
    }

    protected dateChanged() {
        const serviceValidStart = this.service.definition?.dateRange?.start || null;
        const serviceValidEnd = this.service.definition?.dateRange?.end || null;
        const authorizedDate = this.service.authorizedDate || null;
        const expirationDate = this.service.expirationDate || null;
        const allowNullDateOfService = this.service.status?.determination?.status?.flag?.allowNullDateOfService || false;

        //Dates are ALWAYS valid when allowNullDates are true and they pass the checkForContainingDates.
        if((this.servicesAuthDate?.reqDet || this.servicesAuthDate?.reqDataEntry) && !authorizedDate && !allowNullDateOfService) {
            this.hasValidDates = false;
        } else if((this.servicesExpDate?.reqDet || this.servicesExpDate?.reqDataEntry) && !expirationDate && !allowNullDateOfService) {
            this.hasValidDates = false;
        } else if(!DateComparison.checkForContainingDates(serviceValidStart, serviceValidEnd, authorizedDate || serviceValidStart, expirationDate || serviceValidEnd)) {
            this.hasValidDates = false;
        } else {
            this.hasValidDates = true;
        }
    }

    protected async removeModifier(modifier: CD.AuthModifier): Promise<void> {
        if(!this.service || !modifier) { return; }

        if(!!this.service.modifiers) {
            this.service.modifiers = this.service.modifiers.filter(m => m.id !== modifier.id);
        }
    }

    ////****************** Functions ************************************************////
    protected configureTransitions(): void {
        // service should only be able to change status to those among the current status's transitions
        if(!this.service.status?.transitions) {
            // if there are no transitions, use all transitions
            this.transitions = deepClone(this.allTransitions);
            return;
        };
        this.transitions = deepClone(this.service.status.transitions);
        // include the current status among the list of transitions so that dropdown has an initial value
        const currentStatus = this.service.status;
        const currentStatusFound = this.transitions.some(status => status.id === currentStatus.id);
        if(currentStatusFound) return;
        this.transitions.push(currentStatus);
        this.transitions.sort((a, b) => {
            if(!a.description || !b.description) return 0;
            if(a.description < b.description) return -1;
            if(a.description > b.description) return 1;
            return 0;
        });
    }

    protected async selectModifier(modifier: CD.AuthModifier): Promise<void> {
        if(!this.service || !modifier) { return; }

        if(!!this.service.modifiers) {
            this.service.modifiers.push(modifier);
            this.modifierSearchQuery = null;
            this.modifierSearchResults.splice(0);
        }
    }

    protected filterModifiers = (modifier: CD.AuthModifier): boolean => {
        if(!this.service.modifiers && !modifier) { return false; }

        return !this.service?.modifiers?.some(m => m.id === modifier.id);
    }

    private constructSaveObject(): UM.AuthService {
        if(!this.service.id) throw new Error('Bindable property service must be bound and include an id');
        if(!this.service.quantity) throw new Error('Bindable property service must be bound and include quantity');

        const service: UM.AuthService = {
            id: this.service.id,
            quantity: {
                approved: this.service.quantity.approved || 0,
                requested: this.requestedCount || 1
            },
            modifiers: getReferences(this.service.modifiers),
            authorizedDate: this.service.authorizedDate || null,
            expirationDate: this.service.expirationDate || null,
            status: this.selectedStatus?.code ? { code: this.selectedStatus.code } : null,
            sequence: this.service.sequence,
            sequenceFlag: this.service.sequenceFlag,
            flag: {
                includeInCorrespondence: true
            }
        };

        if(this.options.pendServiceChanges) {
            service.definition = {
                code: this.service.definition?.code,
                description: this.service.definition?.description
            };
        }

        service.modifiers = !!this.service.modifiers?.length ? this.service.modifiers.map(modifier => ({
            id: modifier.id,
            description: modifier.description
        })) : [];

        if(this.servicesDental) {
            if(!!this.bodySite) {
                service.bodySite = {
                    id: this.bodySite?.id,
                    description: this.bodySite?.description
                };
            } else {
                service.bodySite = null;
            }
            if(!!this.bodySubSite) {
                service.bodySubSite = {
                    id: this.bodySubSite?.id,
                    description: this.bodySubSite?.description
                };
            } else {
                service.bodySubSite = null;
            }
        } else {
            service.charge = {
                approved: this.service.charge?.approved,
                requested: this.requestedCharge?.toString()
            }
        }

        if(service?.status && this.options.showStatusReason) {
            const statusReason = getReference(this.selectedStatusReason);
            service.status.reason = statusReason ?? null;
        }

        return service;
    }

    ////****************** IForm ****************************************************////
    public hasChanges(): boolean {
        throw new Error('Method not implemented.');
    }

    public async submit(): Promise<boolean> {
        const saveService = this.constructSaveObject();
        return await this.saveService(saveService);
    }

    public reset(): void {
        this.service = { id: null };
        this.requestedCount = 1;
        this.requestedCharge = null;
        this.selectedStatus = null;
        this.selectedStatusReason = null;
        this.bodySite = null;
        this.bodySubSite = null;
        this.modifierSearchQuery = null;
    }

    ////****************** Service Calls ********************************************////
    @apiCall
    private async loadBodySites() {
        try {
            this.state.beginLoad();

            const fields = ['description'];
            const bodySiteTypeCode = 'Dental';

            const [bodySites, bodySubSites] = await Promise.all(
                [
                    this.lookupService.loadBodySites({ all: true }, [], fields, bodySiteTypeCode),
                    this.lookupService.loadBodySubSites({ all: true }, [], fields, bodySiteTypeCode)
                ]
            );

            this.bodySites = bodySites.results;
            this.bodySubSites = bodySubSites.results;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }

    @apiCall
    protected async searchModifiers(value: string): Promise<void> {
        if(!value) {
            if(this.modifierSearchResults) this.modifierSearchResults = [];
            return;
        }

        try {
            this.state.beginLoad();
            const fields = ['description'];
            const response = await this.authService.searchAuthModifiers(value, undefined, undefined, fields);
            this.modifierSearchResults = response.results;
        } catch(error) {
            throw error;
        } finally {
            this.state.endLoad();
        }
    }

    @apiCall
    private async saveService(service: UM.AuthService): Promise<boolean> {
        // If Pending Change, push service changes to Pending Changes array instead
        if(this.options.pendServiceChanges) {
            if(!this.pendingServiceChanges) this.pendingServiceChanges = [];
            if(!service.auth?.id && this.auth?.id) service.auth = { id: this.auth.id };
            this.pendingServiceChanges.push({ service, type: ActionOptions.Update });
            return true;
        }

        let isSuccessful = false;
        try {
            this.state.beginSave();

            const response = await this.authService.saveAuthService(service);
            isSuccessful = response.isSuccessful;
        }
        catch(error) {
            this.toaster.error().message((error as { message: string }).message);
            throw error;
        }
        finally {
            this.toaster.standard.saved('um:service.$', isSuccessful);
            this.state.endSave();
            return isSuccessful;
        }
    }
}

